import React, { useState, useEffect } from 'react';
import { X, User, Calendar, Heart, MessageCircle, Share } from 'lucide-react';

interface PostModalProps {
  postId: string;
  onClose: () => void;
}

interface PostData {
  id: string;
  content: string;
  image?: string;
  likes_count: number;
  comments_count: number;
  created_at: string;
  author: {
    id: string;
    username: string;
    first_name: string;
    last_name: string;
    profile_picture?: string;
    user_type: string;
  };
}

interface Comment {
  id: string;
  content: string;
  created_at: string;
  author: {
    id: string;
    username: string;
    first_name: string;
    last_name: string;
    profile_picture?: string;
    user_type: string;
  };
}

export const PostModal: React.FC<PostModalProps> = ({ postId, onClose }) => {
  const [post, setPost] = useState<PostData | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingComments, setLoadingComments] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const API_BASE_URL = 'https://aztrainersbackend.onrender.com';

  useEffect(() => {
    fetchPost();
    fetchComments();
  }, [postId]);

  const fetchPost = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/posts/${postId}/`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const postData = await response.json();
        setPost(postData);
      } else {
        setError('Failed to load post');
      }
    } catch (err) {
      setError('Error loading post');
      console.error('Error fetching post:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    setLoadingComments(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/posts/${postId}/comments/`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const commentsData = await response.json();
        setComments(commentsData.comments || []);
      } else {
        setComments([]);
      }
    } catch (err) {
      console.error('Error fetching comments:', err);
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFullName = (firstName: string, lastName: string) => {
    return `${firstName} ${lastName}`.trim() || 'Unknown User';
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (loading) {
    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={handleBackdropClick}
      >
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E6A756] mx-auto"></div>
          <p className="mt-4 text-gray-600 text-center">Loading post...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={handleBackdropClick}
      >
        <div className="bg-white rounded-lg p-8 max-w-md">
          <div className="text-center">
            <div className="text-red-500 text-4xl mb-4">⚠️</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={onClose}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg w-full max-w-2xl my-8">
        {/* Header */}
        <div className="relative p-6 border-b">
          <button
            onClick={onClose}
            className="bg-[#E6A756] text-[#5D4037] px-4 py-2 rounded-lg hover:bg-[#D2824C] font-medium"
          >
            <X className="h-6 w-6" />
          </button>
          <h2 className="text-2xl font-bold text-gray-900">Post</h2>
        </div>

        {/* Post Content */}
        <div className="p-6">
          {/* Author Info */}
          <div className="flex items-center space-x-4 mb-6">
            <div className="flex-shrink-0">
              {post.author.profile_picture ? (
                <img
                  src={post.author.profile_picture}
                  alt={getFullName(post.author.first_name, post.author.last_name)}
                  className="w-12 h-12 rounded-full object-cover"
                />
              ) : (
                <div className="w-12 h-12 bg-gradient-to-br from-[#E6A756] to-[#D2824C] rounded-full flex items-center justify-center">
                  <User className="h-6 w-6 text-white" />
                </div>
              )}
            </div>

            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900">
                {getFullName(post.author.first_name, post.author.last_name)}
              </h3>
              <p className="text-gray-600">@{post.author.username}</p>

              <div className="flex items-center space-x-2 text-sm text-gray-500 mt-1">
                <Calendar className="h-4 w-4" />
                <span>{formatDate(post.created_at)}</span>
              </div>
            </div>

            <span className={`px-3 py-1 rounded-full text-sm font-medium ${post.author.user_type === 'trainer'
              ? 'bg-[#B8C5A6] text-[#5D4037]'
              : post.author.user_type === 'admin'
                ? 'bg-[#8B5A3C] text-[#FEFCF8]'
                : 'bg-[#F8F4EE] text-[#5D4037] border border-[#D4C4B0]'
              }`}>
              {post.author.user_type.charAt(0).toUpperCase() + post.author.user_type.slice(1)}
            </span>
          </div>

          {/* Post Content */}
          <div className="mb-6">
            <p className="text-gray-800 text-lg leading-relaxed mb-4">
              {post.content}
            </p>

            {post.image && (
              <img
                src={post.image}
                alt="Post image"
                className="w-full max-h-96 object-cover rounded-lg"
              />
            )}
          </div>

          {/* Engagement Stats */}
          <div className="flex items-center space-x-6 py-4 border-y border-gray-200">
            <div className="flex items-center">
              <Heart className="h-5 w-5 text-red-500 mr-2" />
              <span className="text-gray-700 font-medium">{post.likes_count} likes</span>
            </div>
            <div className="flex items-center">
              <MessageCircle className="h-5 w-5 text-blue-500 mr-2" />
              <span className="text-gray-700 font-medium">{post.comments_count} comments</span>
            </div>
            <div className="flex items-center">
              <Share className="h-5 w-5 text-gray-500 mr-2" />
              <span className="text-gray-700 font-medium">Share</span>
            </div>
          </div>

          {/* Comments Section */}
          <div className="mt-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">
              Comments ({comments.length})
            </h4>

            {loadingComments ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E6A756] mx-auto"></div>
                <p className="mt-2 text-gray-600 text-sm">Loading comments...</p>
              </div>
            ) : comments.length > 0 ? (
              <div className="space-y-4 max-h-64 overflow-y-auto">
                {comments.map((comment) => (
                  <div key={comment.id} className="flex space-x-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-shrink-0">
                      {comment.author.profile_picture ? (
                        <img
                          src={comment.author.profile_picture}
                          alt={getFullName(comment.author.first_name, comment.author.last_name)}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 bg-gradient-to-br from-[#E6A756] to-[#D2824C] rounded-full flex items-center justify-center">
                          <User className="h-4 w-4 text-white" />
                        </div>
                      )}
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h5 className="text-sm font-medium text-gray-900">
                          {getFullName(comment.author.first_name, comment.author.last_name)}
                        </h5>
                        <span className="text-xs text-gray-500">
                          @{comment.author.username}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatDate(comment.created_at)}
                        </span>
                      </div>
                      <p className="text-gray-700 text-sm">
                        {comment.content}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <MessageCircle className="h-12 w-12 mx-auto mb-3 text-[#D4C4B0]" />
                <p>No comments yet</p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex space-x-4">
            <button className="flex items-center px-4 py-2 bg-[#D2824C] text-[#FEFCF8] rounded-lg hover:bg-[#A0673D] transition-colors font-medium">
              <Heart className="h-4 w-4 mr-2" />
              Like
            </button>
            <button className="flex items-center px-4 py-2 bg-[#9CAF88] text-[#FEFCF8] rounded-lg hover:bg-[#7A8471] transition-colors font-medium">
              <MessageCircle className="h-4 w-4 mr-2" />
              Comment
            </button>
            <button className="flex items-center px-4 py-2 bg-[#F8F4EE] text-[#5D4037] rounded-lg hover:bg-[#F5EFE7] transition-colors font-medium border border-[#D4C4B0]">
              <Share className="h-4 w-4 mr-2" />
              Share
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};